import { es } from '../i18n/es';
import { createLocalStorageAdapter } from './local-storage';
import { createMemoryStorage } from './memory-storage';
import type { StorageAdapter } from './storage-adapter';

export const PROBE_KEY = 'narcoverse:probe';

export type StorageMode = 'persistent' | 'memory';

export interface StorageUnavailableEvent {
  type: 'STORAGE_UNAVAILABLE';
}

/** The adapter the save logic uses, plus what the interface needs to know about it. */
export interface SaveStorage {
  readonly adapter: StorageAdapter;
  readonly mode: StorageMode;
  /** Permanent notice (GDD §6.4) once the session runs in memory; null while persisting. */
  readonly notice: string | null;
}

/**
 * Picks the persistent adapter when the browser storage answers a probe, memory otherwise. If a write
 * fails later, the known documents move to memory for the rest of the session. `onUnavailable` fires once.
 */
export function initStorage(
  getBrowserStorage: () => Storage | undefined,
  onUnavailable: (event: StorageUnavailableEvent) => void = () => undefined,
): SaveStorage {
  const memory = createMemoryStorage();
  const known = new Map<string, string>();
  let persistent = openPersistent(getBrowserStorage);
  let mode: StorageMode = persistent ? 'persistent' : 'memory';

  const fallBack = (pending?: { key: string; value: string }) => {
    if (mode === 'memory' && !persistent) return;
    for (const [key, value] of known) memory.set(key, value);
    if (pending) memory.set(pending.key, pending.value);
    persistent = undefined;
    mode = 'memory';
    onUnavailable({ type: 'STORAGE_UNAVAILABLE' });
  };

  const adapter: StorageAdapter = {
    get(key) {
      if (persistent) {
        try {
          const value = persistent.get(key);
          if (value !== null) known.set(key, value);
          return value;
        } catch {
          fallBack();
        }
      }
      return memory.get(key);
    },
    set(key, value) {
      if (persistent) {
        try {
          persistent.set(key, value);
          known.set(key, value);
          return;
        } catch {
          fallBack({ key, value });
          return;
        }
      }
      memory.set(key, value);
    },
    remove(key) {
      known.delete(key);
      if (persistent) {
        try {
          persistent.remove(key);
          return;
        } catch {
          fallBack();
        }
      }
      memory.remove(key);
    },
  };

  if (!persistent) onUnavailable({ type: 'STORAGE_UNAVAILABLE' });

  return {
    adapter,
    get mode() {
      return mode;
    },
    get notice() {
      return mode === 'memory' ? es['ui.save.unavailable'] : null;
    },
  };
}

function openPersistent(getBrowserStorage: () => Storage | undefined): StorageAdapter | undefined {
  try {
    const storage = getBrowserStorage();
    if (!storage) return undefined;
    const adapter = createLocalStorageAdapter(storage);
    adapter.set(PROBE_KEY, '1');
    adapter.remove(PROBE_KEY);
    return adapter;
  } catch {
    return undefined;
  }
}
